/**
 * @module modules/side-panel-sk
 * @description <h2><code>side-panel-sk</code></h2>
 *
 * Element for showing the legend next to plot-google-chart-sk.
 * The side panel comes with a left bar that will close or open the
 * side panel. This component is used by plot-google-chart-sk.
 *
 * When there is only one trace in the dataframe, the legend will be empty.
 * When the legend is empty, the side panel will have no content.
 * It is recommended to hide this module if there is no content to show.
 *
 * This side panel can be adapted to also show the tooltip rather
 * than have the tooltip hover over the data point.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "SidePanelSk", {
    enumerable: true,
    get: function() {
        return SidePanelSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _classmap = require("lit/directives/class-map.js");
const _context = require("@lit/context");
const _dataframe_context = require("../dataframe/dataframe_context");
const _traceset = require("../dataframe/traceset");
require("@material/web/button/outlined-button.js");
require("@material/web/iconbutton/icon-button.js");
require("@material/web/icon/icon.js");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _SidePanelSk;
const chevronLeft = (0, _lit.html)`<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 24 24"
  fill="currentColor">
  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
</svg>`;
const chevronRight = (0, _lit.html)`<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 24 24"
  fill="currentColor">
  <path d="M8.59 7.41L10 6l6 6-6 6-1.41-1.41L13.17 12z" />
</svg>`;
const unwantedKeys = new Set([
    'master',
    'improvement_direction',
    'unit'
]);
let SidePanelSk = (_SidePanelSk = class SidePanelSk extends _lit.LitElement {
    willUpdate(changedProperties) {
        if (changedProperties.has('traceColorMap') || changedProperties.has('data') && this.data) {
            this.updateLegendItems();
        }
    }
    render() {
        const allItemsChecked = this.legendItems.length > 0 && this.legendItems.every((i)=>i.checked);
        return (0, _lit.html)`
      <div
        class="show-hide-bar"
        @click=${this.toggleSidePanel}
        title=${this.opened ? 'Close panel' : 'Open panel'}>
        <md-icon>${this.opened ? chevronRight : chevronLeft}</md-icon>
      </div>
      <div class="info ${(0, _classmap.classMap)({
            closed: !this.opened
        })}">
        <div class="delta-range" ?hidden=${!this.showDelta}>
          Delta: ${this.deltaRaw}<br />
          Percentage: ${this.deltaPercentage}
        </div>
        <div class="label-key-title">
          <span>${this.legendKeysFormat}</span>
        </div>
        <div class="select-all-checkbox">
          <label>
            <input
              type="checkbox"
              id="header-checkbox"
              .checked=${allItemsChecked}
              @click=${this.toggleAllCheckboxes} />
            Select all
          </label>
        </div>
        <div id="rows">
          <ul>
            ${this.legendItems.map((item, index)=>{
            const handleCheck = (e)=>{
                const checkEvent = e.target;
                this.toggleItemChecked(item, checkEvent.checked);
            };
            const checkedCount = this.legendItems.filter((i)=>i.checked).length;
            const isLastChecked = item.checked && checkedCount === 1;
            return (0, _lit.html)`
                <li style="color: ${item.color}">
                  <label class="${item.highlighted ? 'highlight' : 'default'}">
                    <input
                      type="checkbox"
                      id="id-${index}"
                      @click=${handleCheck}
                      .checked=${item.checked}
                      ?disabled=${isLastChecked}
                      title=${isLastChecked ? 'At least one trace must be selected.' : 'Select/Unselect this value from the graph'} />
                    ${item.displayName}
                  </label>
                </li>
              `;
        })}
          </ul>
        </div>
      </div>
    `;
    }
    /**
   * Toggles the checked state of a legend item and dispatches an event.
   *
   * @param item - The legend item to toggle.
   * @param isChecked - The new checked state.
   */ toggleItemChecked(item, isChecked) {
        const checkedCount = this.legendItems.filter((i)=>i.checked).length;
        if (!isChecked && checkedCount <= 1) {
            // Don't allow unchecking the last item. Re-render to revert checkbox state.
            this.requestUpdate();
            return;
        }
        item.checked = isChecked;
        this.checkboxDispatchHandler(isChecked, item.labels);
        // Update header checkbox
        const headerCheckbox = this.renderRoot.querySelector('#header-checkbox');
        if (headerCheckbox) {
            headerCheckbox.checked = this.legendItems.every((i)=>i.checked);
        }
        this.requestUpdate();
    }
    /**
   * Sets the checkbox state for the given trace id.
   * @param checked Whether the boxed is checked or not.
   * @param traceId The trace id.
   */ SetCheckboxForTrace(checked, traceId) {
        const item = this.legendItems.find((i)=>i.labels.includes(traceId));
        if (item) {
            this.toggleItemChecked(item, checked);
        }
    }
    /**
   * Sets all the checkboxes in the panel to the given checked state.
   * @param checked The desired state of the checkboxes.
   */ SetAllBoxes(checked) {
        const changedLabels = [];
        const itemsToChange = [];
        if (checked) {
            this.legendItems.forEach((item)=>{
                if (!item.checked) {
                    itemsToChange.push(item);
                }
            });
        } else {
            // Unchecking: don't uncheck the last item.
            const checkedItems = this.legendItems.filter((item)=>item.checked);
            if (checkedItems.length > 1) {
                // Uncheck all but the last one.
                for(let i = 0; i < checkedItems.length - 1; i++){
                    itemsToChange.push(checkedItems[i]);
                }
            }
        }
        itemsToChange.forEach((item)=>{
            item.checked = checked;
            changedLabels.push(...item.labels);
        });
        if (changedLabels.length > 0) {
            this.checkboxDispatchHandler(checked, changedLabels);
        }
        this.requestUpdate();
    }
    /**
   * Highlight the traces on the given indices on the panel.
   * @param traceIndices Indices of the traces to highlight.
   */ HighlightTraces(traceIndices) {
        this.legendItems.forEach((item)=>item.highlighted = false);
        if (!this.data) {
            return;
        }
        traceIndices.forEach((traceIndex)=>{
            const label = this.data.getColumnLabel(traceIndex + 2);
            const item = this.legendItems.find((i)=>i.labels.includes(label));
            if (item) {
                item.highlighted = true;
            }
        });
        this.requestUpdate();
    }
    toggleAllCheckboxes() {
        const headerCheckbox = this.renderRoot.querySelector(`#header-checkbox`);
        const isChecked = headerCheckbox.checked;
        const changedLabels = [];
        const itemsToChange = [];
        if (isChecked) {
            this.legendItems.forEach((item)=>{
                if (!item.checked) {
                    itemsToChange.push(item);
                }
            });
        } else {
            // Unchecking: don't uncheck the last item.
            const checkedItems = this.legendItems.filter((item)=>item.checked);
            if (checkedItems.length <= 1) {
                // Revert the checkbox state since we can't uncheck the last item.
                headerCheckbox.checked = true;
                return;
            }
            // Uncheck all but the last one.
            for(let i = 0; i < checkedItems.length - 1; i++){
                itemsToChange.push(checkedItems[i]);
            }
        }
        itemsToChange.forEach((item)=>{
            item.checked = isChecked;
            changedLabels.push(...item.labels);
        });
        if (changedLabels.length > 0) {
            this.checkboxDispatchHandler(isChecked, changedLabels);
        }
        this.requestUpdate();
    }
    toggleSidePanel() {
        this.opened = !this.opened;
        this.dispatchEvent(new CustomEvent('side-panel-toggle', {
            bubbles: true,
            composed: true,
            detail: {
                open: this.opened
            }
        }));
    }
    updateLegendItems() {
        if (!this.data) {
            this.legendItems = [];
            return;
        }
        const getLegendData = (0, _traceset.getLegend)(this.data);
        if (getLegendData.length === 0) {
            this.legendItems = [];
            return;
        }
        // Determine the ordered list of keys to display from the first legend item.
        // `getLegend` ensures all items have the same keys in the same sorted order.
        const allKeys = Object.keys(getLegendData[0]);
        const displayKeys = [];
        // Special handling for 'test' key to ensure it's always first if it exists.
        if (allKeys.includes('test')) {
            displayKeys.push('test');
        }
        // Add remaining keys, excluding unwanted ones and 'test' (if already added).
        allKeys.forEach((key)=>{
            if (!unwantedKeys.has(key) && key !== 'test') {
                displayKeys.push(key);
            }
        });
        this.legendKeysFormat = displayKeys.join('/');
        // Generate the display name for each legend item using the determined key order.
        const displayLegendList = getLegendData.map((legendEntryObj)=>{
            const legendEntry = legendEntryObj;
            return displayKeys.map((key)=>legendEntry[key]).filter((value)=>value).join('/');
        });
        // Group trace labels by their generated display name.
        const legendToLabelsMap = new Map();
        const numCols = this.data.getNumberOfColumns();
        for(let i = 2; i < numCols; i++){
            const label = this.data.getColumnLabel(i);
            const displayName = displayLegendList[i - 2];
            if (!legendToLabelsMap.has(displayName)) {
                legendToLabelsMap.set(displayName, []);
            }
            legendToLabelsMap.get(displayName).push(label);
        }
        // Create the final list of legend items for rendering.
        const newLegendItems = [];
        const uniqueDisplayNames = [
            ...legendToLabelsMap.keys()
        ];
        uniqueDisplayNames.forEach((displayName)=>{
            var _existingItem, _existingItem1;
            const labels = legendToLabelsMap.get(displayName);
            // Preserve state from existing items.
            const existingItem = this.legendItems.find((item)=>item.displayName === displayName);
            const color = this.traceColorMap.get(labels[0]) || 'black';
            var _existingItem_checked, _existingItem_highlighted;
            newLegendItems.push({
                displayName: displayName,
                labels: labels,
                color: color,
                checked: (_existingItem_checked = (_existingItem = existingItem) === null || _existingItem === void 0 ? void 0 : _existingItem.checked) !== null && _existingItem_checked !== void 0 ? _existingItem_checked : true,
                highlighted: (_existingItem_highlighted = (_existingItem1 = existingItem) === null || _existingItem1 === void 0 ? void 0 : _existingItem1.highlighted) !== null && _existingItem_highlighted !== void 0 ? _existingItem_highlighted : false
            });
        });
        this.legendItems = newLegendItems.sort((a, b)=>{
            // Sort by display name, then by first label for consistent ordering.
            return a.displayName.localeCompare(b.displayName) || a.labels[0].localeCompare(b.labels[0]);
        });
    }
    checkboxDispatchHandler(isSelected, labels) {
        const detail = {
            selected: isSelected,
            labels: labels
        };
        this.dispatchEvent(new CustomEvent('side-panel-selected-trace-change', {
            detail,
            bubbles: true
        }));
    }
    constructor(...args){
        super(...args);
        // Manages the state of the side panel as collapsed or open.
        this.opened = true;
        this.deltaRaw = null;
        this.deltaPercentage = null;
        // Display delta-range if the value is true, vice versa.
        // Default is false, which means hide delta-range information
        this.showDelta = false;
        this.legendItems = [];
        this.traceColorMap = new Map();
        this.legendKeysFormat = '';
    }
}, (()=>{
    _SidePanelSk.styles = (0, _lit.css)`
    :host {
      display: flex;
      height: 100%;
      border-radius: 8px;
      overflow: scroll; /* legend entries can be very long */
      box-shadow:
        0 2px 1px -1px rgb(0 0 0 / 20%),
        0 1px 1px 0 rgb(0 0 0 / 14%),
        0 1px 3px 0 rgb(0 0 0 / 12%); /* Elevation shadow */
    }

    .show-hide-bar {
      width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .show-hide-bar:hover {
      background-color: gray;
    }

    .label-key-title {
      color: #274878; /* Hex blue, which aligns with the graph title's color */
      padding-top: 10px;
      padding-left: 5px;
      padding-bottom: 5px;
      position: relative;
    }

    .delta-range {
      color: #274878; /* Hex blue, which aligns with the graph title's color */
      padding-top: 10px;
      padding-left: 5px;
      padding-bottom: 5px;
      position: relative;
    }

    .info.closed {
      display: none;
    }

    .select-all-checkbox {
      color: #274878; /* Hex blue, which aligns with the graph title's color */
      display: flex;
      padding-left: 5px;
    }

    ul {
      list-style: none; /* Remove default bullet points */
      padding-left: 5px;
      font-size: 15px;
      margin-block-start: 3px;
    }

    .default {
      border-style: none;
    }

    .highlight {
      border-style: solid;
    }
  `;
})(), _SidePanelSk);
_ts_decorate([
    (0, _decorators.property)({
        reflect: true,
        type: Boolean
    })
], SidePanelSk.prototype, "opened", void 0);
_ts_decorate([
    (0, _context.consume)({
        context: _dataframe_context.dataTableContext,
        subscribe: true
    }),
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", typeof _dataframe_context.DataTable === "undefined" ? Object : _dataframe_context.DataTable)
], SidePanelSk.prototype, "data", void 0);
_ts_decorate([
    (0, _decorators.property)({
        reflect: true
    }),
    _ts_metadata("design:type", Object)
], SidePanelSk.prototype, "deltaRaw", void 0);
_ts_decorate([
    (0, _decorators.property)({
        reflect: true
    }),
    _ts_metadata("design:type", Object)
], SidePanelSk.prototype, "deltaPercentage", void 0);
_ts_decorate([
    (0, _decorators.property)({
        reflect: true
    })
], SidePanelSk.prototype, "showDelta", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], SidePanelSk.prototype, "legendItems", void 0);
_ts_decorate([
    (0, _context.consume)({
        context: _dataframe_context.traceColorMapContext,
        subscribe: true
    }),
    (0, _decorators.property)({
        attribute: false
    })
], SidePanelSk.prototype, "traceColorMap", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false,
        reflect: true
    })
], SidePanelSk.prototype, "legendKeysFormat", void 0);
SidePanelSk = _ts_decorate([
    (0, _decorators.customElement)('side-panel-sk')
], SidePanelSk);
